import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import { DRAW_TOOLS, type DrawTool, type ShapeKind } from '../doc/schema';
import type { CutoutMode } from './cutout';
import { effectiveTheme } from '../theme/theme';

/** Default colours offered everywhere a colour is picked. */
export const PALETTE = [
  '#111111',
  '#ffffff',
  '#8d8d8d',
  '#d7263d',
  '#f46036',
  '#ffd23f',
  '#3bb273',
  '#1b998b',
  '#2b5bd7',
  '#7b2cbf',
  '#ff6fb5',
  '#5b3a29',
] as const;

/** Modes of the draw tool: the ink tools, the eraser, the lasso that selects strokes, and the colour picker. */
export type PenMode = DrawTool | 'eraser' | 'lasso' | 'picker';
const PEN_MODES: readonly string[] = [...DRAW_TOOLS, 'eraser', 'lasso', 'picker'];

export const isInkMode = (mode: PenMode): mode is DrawTool => (DRAW_TOOLS as readonly string[]).includes(mode);

/** How one ink tool draws. Size is in screen pixels at the zoom the stroke is drawn at. */
export interface InkStyle {
  color: string;
  size: number;
  /** 0..1 */
  opacity: number;
}

/** Per-device tool defaults (persisted in localStorage, not part of the board). */
export interface ToolSettings {
  shapeKind: ShapeKind;
  fill: string;
  stroke: string;
  strokeWidth: number;
  penMode: PenMode;
  /** The ink tool used last: the one the colour picker picks for, and returns to. */
  inkTool: DrawTool;
  ink: Record<DrawTool, InkStyle>;
  eraserSize: number;
  /** The eraser removes every stroke it touches whole, instead of only the part under it. */
  eraserWhole: boolean;
  /** Pausing at the end of a line straightens it (Shift does too, in 15 degree steps). */
  holdStraighten: boolean;
  /** Stylus mode: a single finger never draws or moves things; two fingers still pan/zoom. */
  penOnly: boolean;
  /** Magnetic snapping to other items' edges and centres while moving/resizing (Alt bypasses). */
  snap: boolean;
  /** Also snap to the canvas grid. */
  snapGrid: boolean;
  /** Image imports: re-encode the stored original as lossy WebP (animated GIFs and attachments are left alone). */
  importWebp: boolean;
  /** WebP quality, 0..1. Dimensions are never changed by this; downscaling is a per-board setting. */
  importQuality: number;
  /** Cutout tool: outline shape, soft edge (world units) and whether the source keeps a hole. */
  cutoutMode: CutoutMode;
  cutoutFeather: number;
  cutoutPunch: boolean;
}

const KEY = 'vds:tools';

// ink that is visible on the canvas of the theme in use when the settings were first created
const INK_COLOR = effectiveTheme() === 'dark' ? '#ececec' : '#111111';

const defaults: ToolSettings = {
  shapeKind: 'rect',
  fill: 'none',
  stroke: '#2b5bd7',
  strokeWidth: 3,
  penMode: 'pen',
  inkTool: 'pen',
  ink: {
    pen: { color: INK_COLOR, size: 4, opacity: 1 },
    fineliner: { color: INK_COLOR, size: 2, opacity: 1 },
    brush: { color: INK_COLOR, size: 8, opacity: 1 },
    highlighter: { color: '#ffd23f', size: 24, opacity: 1 },
    fill: { color: '#8d8d8d', size: 0, opacity: 1 },
  },
  eraserSize: 24,
  eraserWhole: false,
  holdStraighten: true,
  penOnly: false,
  snap: true,
  snapGrid: false,
  importWebp: true,
  importQuality: 0.9,
  cutoutMode: 'lasso',
  cutoutFeather: 0,
  cutoutPunch: false,
};

/** Settings saved before each ink tool had its own style kept the pen and highlighter flat. */
interface LegacyInk {
  penColor?: string;
  penSize?: number;
  highlighterColor?: string;
  highlighterSize?: number;
}

function load(): ToolSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const { penColor, penSize, highlighterColor, highlighterSize, ...saved } = JSON.parse(raw) as Partial<ToolSettings> & LegacyInk;
      const ink = {} as Record<DrawTool, InkStyle>;
      for (const t of DRAW_TOOLS) ink[t] = { ...defaults.ink[t], ...saved.ink?.[t] };
      if (!saved.ink) {
        if (penColor) ink.pen.color = penColor;
        if (penSize) ink.pen.size = penSize;
        if (highlighterColor) ink.highlighter.color = highlighterColor;
        if (highlighterSize) ink.highlighter.size = highlighterSize;
      }
      const merged = { ...defaults, ...saved, ink };
      if (!PEN_MODES.includes(merged.penMode)) merged.penMode = defaults.penMode;
      if (!saved.inkTool || !DRAW_TOOLS.includes(merged.inkTool)) merged.inkTool = isInkMode(merged.penMode) ? merged.penMode : defaults.inkTool;
      return merged;
    }
  } catch {
    /* ignore */
  }
  return defaults;
}

export const toolSettings = createStore<ToolSettings>(() => load());

toolSettings.subscribe((s) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
});

export function setToolSettings(patch: Partial<ToolSettings>): void {
  toolSettings.setState(patch);
}

/** Switches the draw tool's mode; an ink tool also becomes the one the colour picker serves. */
export function setPenMode(penMode: PenMode): void {
  toolSettings.setState(isInkMode(penMode) ? { penMode, inkTool: penMode } : { penMode });
}

export function setInkStyle(tool: DrawTool, patch: Partial<InkStyle>): void {
  const { ink } = toolSettings.getState();
  toolSettings.setState({ ink: { ...ink, [tool]: { ...ink[tool], ...patch } } });
}

export function useToolSettings<T>(selector: (s: ToolSettings) => T): T {
  return useStore(toolSettings, selector);
}
